import React, { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { ALL_TABS } from "./tabs";
import { showToast } from "./lib/theme";
import UserControlCenter from "./UserControlCenter";
import { APP_VERSION } from "./lib/version";

// Legacy emoji icons by system role key. Custom roles fall through to a
// neutral 🔖 since admins choose their own Arabic label.
const ROLE_ICONS = {
  admin: "👑",
  ceo: "🎯",
  operations: "⚙",
  branch_manager: "🏪",
  marketing: "🛍",
  accountant: "💰",
  employees_manager: "👥",
  viewer: "👁",
};
const labelOf = (role, rolesList) => {
  const r = (rolesList || []).find((x) => x.role === role);
  const icon = ROLE_ICONS[role] || "🔖";
  return `${icon} ${r?.label || role}`;
};

export default function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", role: "operations", name: "", email: "", full_name: "", phone: "", bio: "", employee_number: "" });
  // Page permissions selected during creation. Pre-populated with the picked
  // role's defaults so admin can immediately submit, but admin can uncheck
  // anything before saving — the explicit list is what's stored so the new
  // user always sees exactly the picked tabs (never a fallback to "all role
  // defaults" which used to leak unintended pages).
  const [newPerms, setNewPerms] = useState(() => new Set(
    ALL_TABS.filter((t) => t.roles.includes("operations")).map((t) => t.id)
  ));
  const toggleNewPerm = (id) => setNewPerms((p) => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectRoleDefault = (role) => {
    const ids = ALL_TABS.filter((t) => t.roles.includes(role)).map((t) => t.id);
    setNewPerms(new Set(ids));
  };
  const clearNewPerms = () => setNewPerms(new Set());
  // When the picked role changes, reset selection to that role's defaults
  // (admin can still tweak). This keeps the form predictable.
  const handleRoleChange = (newRole) => {
    setForm((f) => ({ ...f, role: newRole }));
    selectRoleDefault(newRole);
  };
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  // v140 — مركز التحكم الموحّد: كل إعدادات المستخدم في نافذة واحدة.
  const [controlFor, setControlFor] = useState(null);
  // Employee directory (uploaded via Settings) — used to auto-fill the form.
  const [employees, setEmployees] = useState([]);
  // Dynamic role catalog (system + custom) from /api/rbac/roles
  const [availableRoles, setAvailableRoles] = useState([]);

  const reloadRoles = useCallback(async () => {
    try {
      const { data } = await api.get("/rbac/roles");
      setAvailableRoles(data?.roles || []);
    } catch (_e) { /* silent — admin may not have rbac.manage perm */ }
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/users/unlinked-employees");
        setEmployees(data?.items || []);
      } catch (_e) { /* silent — directory is optional */ }
    })();
  }, []);

  const pickEmployee = (empNo) => {
    const e = employees.find((x) => x.employee_number === empNo);
    if (!e) return;
    const fullName = e.name || "";
    const firstName = (fullName.split(" ")[0] || "").trim();
    setForm((f) => ({
      ...f,
      employee_number: e.employee_number,
      name: firstName || f.name,
      full_name: fullName || f.full_name,
      email: e.email || f.email,
      phone: e.phone || f.phone,
      bio: f.bio || `${e.job_title || ""} · ${e.branch || ""}`.trim().replace(/^· /, "").replace(/ ·\s*$/, ""),
      username: f.username || (e.employee_number ? `emp${e.employee_number}` : (firstName ? firstName.replace(/\s+/g, "_").toLowerCase() : f.username)),
    }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      setMsg({ t: "r", m: formatErr(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); reloadRoles(); }, [reloadRoles]);

  const flash = useCallback((t, m) => {
    setMsg({ t, m });
    setTimeout(() => setMsg(null), 4000);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Always send an explicit page_permissions array so the new user sees
      // exactly the tabs admin picked (no implicit fall-back to "role defaults"
      // which previously caused new users to see all pages until edited).
      // For admin role we still send the full set (admin sees everything anyway).
      const perms = form.role === "admin"
        ? ALL_TABS.map((t) => t.id)
        : Array.from(newPerms);
      const { data } = await api.post("/auth/users", { ...form, page_permissions: perms });
      const emailNote = form.email
        ? (data?.welcome_email_sent ? ` · 📧 إيميل ترحيب أُرسل لـ ${form.email}` : ` · ⚠ الإيميل لم يُرسل (${data?.welcome_email_error || "تحقق من إعدادات Resend"})`)
        : "";
      const permsNote = form.role !== "admin"
        ? ` · 🔐 صلاحيات: ${perms.length} تبويب`
        : "";
      flash("g", `✅ تم إنشاء المستخدم ${form.username}${permsNote}${emailNote}`);
      setForm({ username: "", password: "", role: "operations", name: "", email: "", full_name: "", phone: "", bio: "", employee_number: "" });
      setNewPerms(new Set(ALL_TABS.filter((t) => t.roles.includes("operations")).map((t) => t.id)));
      setShowForm(false);
      // Refresh unlinked Malachite employees list — the newly-created user
      // claimed one of them, so it should disappear from the dropdown.
      try {
        const { data: emp } = await api.get("/auth/users/unlinked-employees");
        setEmployees(emp?.items || []);
      } catch (_e) { /* silent */ }
      // Scroll back to the top of the users panel so the user lands on the
      // refreshed list (and the success flash) instead of a white area below
      // the page — the permissions grid is tall (31 tabs) and after submit
      // the page collapses, leaving the previous scroll position past the
      // new page height.
      setTimeout(() => {
        const panel = document.querySelector('[data-testid="users-panel"]');
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
      load();
    } catch (e) {
      flash("r", formatErr(e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const permsSummary = (u) => {
    if (u.role === "admin") return "كل التبويبات";
    if (!Array.isArray(u.page_permissions)) return "افتراضي الدور";
    return `${u.page_permissions.length} صفحة`;
  };

  return (
    <div className="set-sec" data-testid="users-panel">
      <h4>👥 إدارة المستخدمين</h4>
      {msg && <div className={`al al${msg.t}`}>{msg.m}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--gy)" }}>
          {users.length.toLocaleString("en-US")} مستخدم
        </div>
        <button
          data-testid="btn-toggle-create-user"
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "إلغاء" : "+ مستخدم جديد"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="user-form" data-testid="create-user-form">
          {employees.length > 0 && (
            <div style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)", display: "block", marginBottom: 6 }}>
                🔗 ربط بموظف من Malachite HCM (متاح: {employees.length} موظف غير مرتبط)
              </label>
              <select
                data-testid="new-user-from-employee"
                onChange={(e) => { if (e.target.value) pickEmployee(e.target.value); }}
                value={form.employee_number || ""}
                style={{ width: "100%", padding: 8, fontSize: 13, borderRadius: 6 }}
              >
                <option value="">— اختر موظفاً للربط وملء النموذج تلقائياً —</option>
                {employees.map((e) => (
                  <option key={e.employee_number} value={e.employee_number}>
                    #{e.employee_number} · {e.name} · {e.job_title || "—"} · {e.branch || ""}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4, lineHeight: 1.7 }}>
                ستملأ: الاسم الكامل، البريد، الجوال، اسم المستخدم، النبذة، و <b>رقم الموظف</b>.
                الربط يجعل ملف البونص + الحضور + تاريخ الانضمام يعمل تلقائياً من Malachite HCM.
              </div>
              {form.employee_number && (
                <div data-testid="new-user-linked-badge"
                  style={{ marginTop: 8, padding: "6px 10px", background: "#dcfce7", color: "#166534", borderRadius: 6, fontSize: 12, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>✓ مرتبط بـ Malachite #{form.employee_number}</span>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, employee_number: "" }))}
                    data-testid="new-user-unlink"
                    style={{ background: "transparent", border: "1px solid #166534", color: "#166534", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontSize: 11 }}>
                    ✕ فك الربط
                  </button>
                </div>
              )}
            </div>
          )}
          {employees.length === 0 && (
            <div style={{ background: "#f3f4f6", borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 11.5, color: "var(--muted)", lineHeight: 1.7 }}>
              ℹ️ لا يوجد موظفون غير مرتبطين في Malachite HCM — كل الموظفين لديهم حسابات. لإضافة موظفين جدد قم بمزامنة Malachite من صفحة الموظفين أولاً.
            </div>
          )}
          <div className="user-form-grid">
            <div>
              <label>اسم المستخدم</label>
              <input
                data-testid="new-user-username"
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                minLength={3}
              />
            </div>
            <div>
              <label>كلمة السر</label>
              <input
                data-testid="new-user-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <div>
              <label>الاسم الكامل</label>
              <input
                data-testid="new-user-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label>الدور</label>
              <select
                data-testid="new-user-role"
                value={form.role}
                onChange={(e) => handleRoleChange(e.target.value)}
              >
                {(availableRoles.length > 0 ? availableRoles : [{ role: "operations", label: "تشغيلي" }, { role: "marketing", label: "تسويقي" }, { role: "viewer", label: "متصفّح" }, { role: "admin", label: "مدير عام" }]).map((r) => (
                  <option key={r.role} value={r.role}>
                    {ROLE_ICONS[r.role] || "🔖"} {r.label || r.role}{!r.is_system ? " (مخصّص)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>الاسم الثلاثي</label>
              <input
                data-testid="new-user-full-name"
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="مثلاً: عبدالله محمد العتيبي"
              />
            </div>
            <div>
              <label>رقم الجوال</label>
              <input
                data-testid="new-user-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="05XXXXXXXX"
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label>📧 البريد الإلكتروني (اختياري — إذا أضفته يُرسل إيميل ترحيب + تعليمات تثبيت)</label>
              <input
                data-testid="new-user-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="example@email.com"
                style={{ width: "100%", direction: "ltr", textAlign: "left" }}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label>📝 نبذة عن المستخدم (يستخدمها الذكاء الاصطناعي لتخصيص تجربته)</label>
              <textarea
                data-testid="new-user-bio"
                rows={2}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="مثلاً: ابن مالك المقهى، يهتم بالتسويق الرقمي وإدارة الحملات، يميل لاتخاذ قرارات سريعة."
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Permissions selector — shown for non-admin roles. Admin always
              has full access regardless of the picked list. */}
          {form.role !== "admin" && (
            <div data-testid="new-user-perms-block"
              style={{ marginTop: 14, background: "var(--beige)", padding: 12, borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                <b style={{ fontSize: 13, color: "var(--brown-deep)" }}>
                  🔐 صلاحيات الصفحات — اختر ما يراه المستخدم على فتحه الأول
                </b>
                <div style={{ display: "flex", gap: 4 }}>
                  <button type="button" className="btn btn-soft" onClick={() => selectRoleDefault(form.role)}
                    data-testid="new-user-perms-role-default"
                    style={{ fontSize: 10, padding: "3px 8px" }}>افتراضي الدور</button>
                  <button type="button" className="btn btn-soft" onClick={() => setNewPerms(new Set(ALL_TABS.map((t) => t.id)))}
                    data-testid="new-user-perms-all"
                    style={{ fontSize: 10, padding: "3px 8px" }}>الكل</button>
                  <button type="button" className="btn btn-soft" onClick={clearNewPerms}
                    data-testid="new-user-perms-clear"
                    style={{ fontSize: 10, padding: "3px 8px" }}>لا شيء</button>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8, lineHeight: 1.6 }}>
                💡 المستخدم الجديد سيرى فقط هذه التبويبات — لا أكثر ولا أقل. يمكنك تعديلها لاحقاً من زر «تعديل» أمام صفه.
                <br />الحالي: <b style={{ color: "var(--brown-deep)" }}>{newPerms.size}</b> تبويب محدد من أصل {ALL_TABS.length}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6 }}>
                {ALL_TABS.map((t) => {
                  const isRoleDefault = t.roles.includes(form.role);
                  return (
                    <label key={t.id}
                      data-testid={`new-user-perm-${t.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "4px 6px", background: "#fff", borderRadius: 4, cursor: "pointer", opacity: isRoleDefault ? 1 : 0.85 }}>
                      <input
                        type="checkbox"
                        checked={newPerms.has(t.id)}
                        onChange={() => toggleNewPerm(t.id)}
                      />
                      <span>{t.label}</span>
                      {!isRoleDefault && <span style={{ fontSize: 9, color: "var(--warn)", marginInlineStart: 4 }}>خارج الدور</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {form.role === "admin" && (
            <div style={{ marginTop: 12, padding: 10, background: "rgba(212,175,55,0.12)", borderRadius: 8, fontSize: 12, color: "var(--brown-deep)" }}>
              👑 المدير يرى كل التبويبات تلقائياً — لا حاجة لاختيار صلاحيات.
            </div>
          )}

          <button data-testid="btn-create-user" type="submit" disabled={busy} className="btn btn-success" style={{ marginTop: 10 }}>
            {busy ? "جارٍ الإنشاء..." : "إنشاء المستخدم"}
          </button>
        </form>
      )}

      <div className="tw" style={{ marginTop: 12 }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--gy)" }}>⏳ جارٍ التحميل...</div>
        ) : (
          <table>
            <thead><tr><th>المستخدم</th><th>الدور</th><th>الوصول</th><th>النسخة</th><th>تاريخ الإنشاء</th><th style={{ textAlign: "center" }}>الإدارة</th></tr></thead>
            <tbody>
              {users.map((u) => {
                const overrides = (u.extra_permissions?.length || 0) + (u.denied_permissions?.length || 0);
                return (
                  <tr key={u.id} data-testid={`user-row-${u.username}`}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,#6B4E3D,#b08756)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
                          {(u.full_name || u.name || u.username).slice(0, 1)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <b style={{ fontSize: 12.5, display: "block" }}>{u.name || u.full_name || u.username}
                            {u.id === currentUser.id && <span style={{ color: "var(--go)", fontSize: 10, marginInlineStart: 5 }}>(أنت)</span>}
                          </b>
                          <span style={{ fontSize: 10.5, color: "var(--muted)" }}>@{u.username}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="b bg" data-testid={`user-role-${u.username}`} style={{ whiteSpace: "nowrap" }}>{labelOf(u.role, availableRoles)}</span>
                    </td>
                    <td style={{ fontSize: 11 }}>
                      <span style={{ color: "var(--muted)" }}>{permsSummary(u)}</span>
                      {overrides > 0 && (
                        <span data-testid={`user-overrides-${u.username}`}
                          style={{ marginInlineStart: 6, fontSize: 10, fontWeight: 800, background: "#EFF6FF", color: "#1D4ED8", padding: "1px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>
                          🎯 {overrides} تخصيص
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 10, whiteSpace: "nowrap" }} data-testid={`user-version-${u.username}`}>
                      {u.last_seen_version ? (
                        <>
                          <span style={{
                            fontWeight: 800, padding: "1px 7px", borderRadius: 9,
                            color: u.last_seen_version === APP_VERSION ? "#166534" : "#B45309",
                            background: u.last_seen_version === APP_VERSION ? "#F0FDF4" : "#FFFBEB",
                          }}>
                            {u.last_seen_version === APP_VERSION ? "✓ محدث" : `⚠ ${u.last_seen_version}`}
                          </span>
                          <div style={{ color: "var(--muted)", marginTop: 2 }}>
                            آخر فتح: {(u.last_seen_at || "").slice(0, 16).replace("T", " ")}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "var(--muted)" }}>— لم يفتح بعد</span>
                      )}
                    </td>
                    <td style={{ fontSize: 10, color: "var(--gy)", whiteSpace: "nowrap" }}>{(u.created_at || "").slice(0, 10)}</td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setControlFor(u)}
                        data-testid={`btn-manage-${u.username}`}
                        title="كل شيء في مكان واحد: الملف · الدور · الصفحات · الصلاحيات الدقيقة · كلمة المرور · الحذف"
                        style={{ fontSize: 11, padding: "5px 14px", whiteSpace: "nowrap" }}
                      >
                        ⚙ إدارة
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {controlFor && (
        <UserControlCenter
          user={controlFor}
          currentUser={currentUser}
          availableRoles={availableRoles}
          onClose={() => setControlFor(null)}
          onSaved={load}
          onDeleted={load}
        />
      )}
    </div>
  );
}
